import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api, { setCsrfToken, registerSessionExpiredHandler, isAuthRejection, isNetworkShapedError, abortAllInFlight } from '../lib/api';
import { NET_VERDICT, subscribeNetworkVerdict, isExplicitlyOffline } from '../lib/offline/networkVerdict';
import { marcarArranquePendiente } from '../lib/offline/arranquePendiente';
import { identifyUser, resetAnalytics, trackEvent } from '../lib/analytics';
import { IS_APP_BUILD } from '../lib/buildTarget';
import { getTokens, setTokens, clearTokens } from '../lib/tokenStore';
import { withNativeSignal } from '../lib/nativeAuth';
import { clearScreenDataCache } from '../lib/screenDataCache';
// TANDA 22 (#214): el aviso de sesión cerrada, para re-hidratar la foto de
// bodega. Módulo HOJA (cero imports propios), así que importarlo aquí no arrastra
// ninguna capa ni crea un ciclo — su propio letrero lo autoriza.
import { SESSION_COMMITTED_EVENT } from '../lib/screenEvents';
import { syncWarehouseIdentity, WAREHOUSE_BOUNDARY, writeUserSnapshot, readOfflineBootUser } from '../lib/warehouse';
import { markSignedInBefore, markGuestBefore } from '../lib/returningUser';
// PILOTO-1C (firma #81): lo ÚNICO que este contexto toma de la superficie de
// grupos, y sólo para el logout — ver su llamada, más abajo.
import { olvidarEmpujonPendiente } from '../lib/groups';
import { logNetError } from '../lib/diag/netError';
import { log as diagLog } from '../lib/diag/readyChainLog';
import { getStudyScreen, registerStudyExitHandler } from '../lib/studyPresence';
// RPW T7 (P2): `examRunsLocally` salió de esta lista con el barrendero de boot
// del examen — era su único gate aquí.
import { anyModeRunsLocally } from '../lib/brain';
// NOTIFICACIONES-1 (firma #464): el push de iOS. Las dos funciones se apagan solas fuera
// del shell nativo, y el plugin se importa diferido DENTRO de `lib/push` (en web, jamás).
import { arrancarPush, darDeBajaPush } from '../lib/push';
import { borrarRutaPendiente, marcarSalida } from '../lib/rutaPendiente';
import { esArranqueDeInvitado, atribucionDelClic } from '../lib/arranqueInvitado';

const AuthContext = createContext(null);
const _authDebug = () => window.location.search.includes('debug=auth');

// F6.α — cadencia del reintento automático del boot "sin conexión" (nativo):
// mientras el estado esté activo, el refresh se reintenta solo cada tanto y
// además al volver el foco/visibilidad. 4s es agresivo a propósito: la meta es
// que al quitar el modo avión la sesión reviva SOLA, sin que el usuario toque nada.
export const BOOT_RETRY_MS = 4000;

/* ── REMATE-266 — EL TECHO DEL CAMINO DE ARRANQUE (sólo app) ──────────────────
 *
 * EL PROBLEMA QUE CIERRA. Las DOS peticiones del arranque —`/auth/refresh` y
 * `/auth/me`— heredaban el techo general de 10 s (`DEFAULT_TIMEOUT_MS`, api.js).
 * Con red zombie las dos salen condenadas: el sistema declara la interfaz viva,
 * así que el atajo de "sin señal" de más abajo no aplica, y el usuario se queda
 * mirando el splash hasta 20 s antes de que la app caiga a su bodega y le sirva
 * algo. Eso, repetido en cada arranque en frío, es literalmente lo que describe
 * "hicieron falta 3 kills y varios minutos".
 *
 * 6 000 ms es el número: sigue siendo generoso —el p99 real de estas dos rutas
 * está muy por debajo, y son las dos peticiones más pequeñas de la app— y
 * convierte el peor arranque en ~12 s en vez de ~20. Y con la evidencia hacia
 * abajo de esta misma tanda (`markNetworkEvidenceDown`), esos dos fallos son
 * justo el par que dictamina DOWN: al segundo, el pill enciende, los candados
 * offline se cierran y la app queda usable desde la bodega — el arranque
 * siguiente ya toma el atajo y ni sale a la red.
 *
 * EL TECHO GLOBAL NO SE MUEVE, y la web tampoco: el gate es `IS_APP_BUILD`, así
 * que en el navegador estas dos llamadas viajan carácter por carácter como hoy.
 * Este techo viaja con `fetchUser`, que además de arrancar es el "refrescar
 * usuario" del cartero de compras; 6 s para un `/auth/me` sigue siendo holgado
 * ahí, y un fallo en ese camino ya se reintenta solo.
 */
export const BOOT_TIMEOUT_MS = 6000;
// post-t7-2-dinero (B6): la relectura del plan al volver al frente, como mucho una vez por minuto.
export const RELECTURA_AL_VOLVER_MS = 60 * 1000;

const bootTimeout = () => (IS_APP_BUILD ? { timeout: BOOT_TIMEOUT_MS } : {});

// ── B-F2 — LA SESIÓN MUERTA NO PATEA A NADIE A MEDIA SESIÓN DE ESTUDIO ──────
//
// EL SÍNTOMA (verificación 1 de la tanda FLASH-3): con una sesión de login
// GENUINAMENTE muerta —el refresh rechazado él mismo con 401/403, no un
// parpadeo— cualquier petición de fondo (cartero, despachador, remitente) basta
// para disparar la cadena api.js:367 → `_onSessionExpired` → `setUser(null)` →
// `ProtectedRoute` (routeGuards.js:22) → `<Navigate to="/signup" />`, que
// desmonta el árbol ENTERO. A media pregunta, eso es «el examen desapareció».
//
// LA FIRMA: con una sesión de estudio EN PANTALLA la expiración se POSPONE. El
// usuario termina su sesión exactamente como siempre —reloj, respuestas, entrega
// manual, vencimiento, resultados— y la expiración se aplica cuando ÉL sale, por
// el MISMO camino de hoy. Sin sesión de estudio en pantalla no cambia nada:
// aplica en el acto, como hoy.
//
// LO QUE NO SE TOCA: la clasificación de errores de api.js (F6.α: red caída,
// timeout, 5xx y cancelaciones siguen SIN expirar nada) queda intacta —esta
// puerta vive por completo del lado de AuthContext, aguas abajo de la decisión
// «la sesión murió», y no opina sobre ella. El refresh exitoso tampoco pasa por
// aquí. Y `clearTokens()` (api.js:371) sigue ocurriendo ANTES, sin cambios: las
// credenciales muertas se tiran igual, lo único que se pospone es la EXPULSIÓN.
// Que las peticiones de fondo sigan devolviendo 401 durante la posposición ya es
// tolerado hoy sin daño: el despachador deja el renglón intacto para el
// siguiente disparador (dispatcher.js:951-957) y el cartero calla y reintenta
// (downloader.js:314-320).
//
// EL GATE es `IS_APP_BUILD`, y hay dos: éste, en el punto de decisión, y el del
// hook de presencia (hooks/useStudyScreenPresence.js), que en web ni siquiera
// escribe el registro. En web todo queda byte-igual — y allá además el carril
// necesita servidor, así que posponer una sesión muerta no tendría sentido.
//
// LA RECULE, Y SU ESTADO VIVO: **ENCENDIDA**. Apagarla devuelve la expulsión
// inmediata de hoy bit a bit: el handler pasa a ser `aplicarExpiracion()` a
// secas, que es el cuerpo de siempre sin una línea cambiada. Es un `const` de
// módulo, no una llave de configuración; el interruptor es de TESTS.
const AUTH_EXPIRY_DEFER = true;
let authExpiryDefer = AUTH_EXPIRY_DEFER;

/** Solo para tests: apagar/encender la posposición y medir la bit-identidad. */
export function __setAuthExpiryDeferForTests(on) {
  authExpiryDefer = on === undefined ? AUTH_EXPIRY_DEFER : !!on;
}

// Los dos escritores del visor, con el contrato de siempre (gate IS_APP_BUILD,
// try/catch que jamás lanza, cero timers, no-op TOTAL en web): hermanos de
// `NAV.PUSH` (StudyCenter) y `NAV.REDIRECT` (ExamSimulator). Con estas dos
// líneas, «me sacó a signup» deja de necesitar autopsia: o hay un par
// DEFER/APPLY —y entonces la expulsión fue ordenada, al salir— o no hay ninguna,
// y entonces la sesión murió sin nadie estudiando.
function logAuthDefer(pantalla) {
  if (!IS_APP_BUILD) return;
  try {
    diagLog('NAV.AUTH.DEFER', { pantalla });
  } catch (e) { /* el visor jamás decide una sesión */ }
}

function logAuthApply(tras) {
  if (!IS_APP_BUILD) return;
  try {
    diagLog('NAV.AUTH.APPLY', { tras });
  } catch (e) { /* el visor jamás decide una sesión */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // F6.α + F6.4b — boot nativo "sin conexión": hay tokens en el Keychain pero
  // el refresh de arranque falló por causa TRANSITORIA (red/timeout/5xx — ver
  // isAuthRejection en lib/api.js). Desde F6.4b la puerta tiene DOS salidas:
  //   - bodega poblada del MISMO dueño → la app ENTRA en modo offline (`user`
  //     sale del snapshot local, `loading` termina, las pantallas pintan de
  //     bodega) y el retry de refresh sigue corriendo de fondo — al volver la
  //     red la sesión se refresca sola, sin recargar pantalla;
  //   - sin bodega utilizable (instalación fresca / otro dueño / sin snapshot)
  //     → `loading` sigue en true y AuthBootGate pinta OfflineBootScreen con
  //     Reintentar, como en F6.α.
  // bootOffline sigue siendo el motor del retry automático en AMBOS casos.
  const [bootOffline, setBootOffline] = useState(false);
  const sessionVersion = useRef(0);
  const bootRetryInFlight = useRef(false);
  // F6.4b — true mientras la sesión vigente ENTRÓ desde la bodega (sin red).
  // Protege contra la expulsión por un /me que falla por red durante el retry
  // y evita re-entradas repetidas. Se resetea en toda frontera de sesión.
  const enteredOfflineRef = useRef(false);
  // Último user conocido, legible desde el handler de sesión expirada (que se
  // registra UNA vez y no ve el estado de React): la frontera de la bodega
  // distingue "expiró un registrado" (conserva) de "expiró un guest" (purga).
  const lastUserRef = useRef(null);
  useEffect(() => { lastUserRef.current = user; }, [user]);
  // B-F2 — hay una expiración esperando a que el usuario salga de su sesión de
  // estudio. REF y no estado, por dos razones y la primera basta: lo leen y lo
  // escriben handlers registrados UNA vez (sesión expirada, salida de pantalla)
  // que jamás verían un estado nuevo; y prenderlo no debe repintar nada —el
  // punto entero de esta pieza es que la pantalla no se entere. Idempotente por
  // construcción: es un booleano, así que N disparos dejan UNA pendiente.
  const expiracionPendienteRef = useRef(false);

  // FRONTERA DE IDENTIDAD — único punto por el que pasa toda transición de
  // sesión (login, guest, verify, OAuth, conversión guest→registrado, logout,
  // sesión expirada; el borrado de cuenta desemboca en logout). Versiona la
  // sesión (descarta respuestas en vuelo de la identidad anterior) Y purga el
  // screenDataCache de F4.3b. Sin la purga, dentro de la ventana de frescura
  // (30s) la identidad nueva pintaba los datos cacheados de la anterior —
  // "docs fantasma" del QA on-device de TestFlight 0.9.0: un guest recién
  // creado veía los documentos de la cuenta que acababa de cerrar sesión
  // (privacidad en dispositivo compartido). No usar sessionVersion.current++
  // directo: cualquier frontera nueva debe pasar por aquí.
  const beginSessionTransition = () => {
    sessionVersion.current++;
    clearScreenDataCache();
    // F6.4b: una frontera de sesión termina cualquier "sesión offline" en
    // curso — la identidad nueva decide su propio camino de boot.
    enteredOfflineRef.current = false;
    // camara-pills (6.3 a): y cualquier arranque pendiente desde la bodega.
    marcarArranquePendiente(false);
  };

  // Applies the server-profile language at session acquisition, through the ONE
  // boot-order rule in lib/language.js (F4.3a-fix). The old inline version here
  // applied the profile to EVERYONE on EVERY fetchUser: /auth/me always returns
  // preferred_language (default "en", guests included), so it stomped the local
  // choice — and its manual localStorage.setItem poisoned the persisted value
  // with "en", which is why no language survived an app restart.
  // applyProfileLanguage skips guests, and skips everything once the user made
  // an explicit in-app choice this run.
  const syncLanguageFromUser = async (userObj) => {
    try {
      const { applyProfileLanguage } = await import('../lib/language');
      applyProfileLanguage(userObj);
    } catch (e) {
      // Silent — language sync is best-effort, must not break auth flow
    }
  };

  // B-F2 — el cuerpo de SIEMPRE de la sesión expirada, sin una línea cambiada:
  // sólo extraído para poder ejecutarlo en DOS momentos (ya mismo, o al salir el
  // usuario de su sesión de estudio). Deps vacías y honestas: no lee estado de
  // React, sólo refs y setters, que son estables. `wasGuest` se lee AQUÍ, en el
  // momento de aplicar, igual que antes — durante una posposición nadie cambia
  // `user`, así que la respuesta es la misma que en el momento del disparo.
  const aplicarExpiracion = useCallback(() => {
    // Frontera HERMANA de la bodega (F6.4a): guest expirado → purga total
    // (la cuenta ya no existe); registrado expirado → conserva, el próximo
    // login decide comparando dueños. Fire-and-forget: nunca lanza y este
    // handler es síncrono.
    const wasGuest = !!lastUserRef.current?.is_guest;
    beginSessionTransition();
    syncWarehouseIdentity(
      wasGuest ? WAREHOUSE_BOUNDARY.SESSION_EXPIRED_GUEST : WAREHOUSE_BOUNDARY.SESSION_EXPIRED
    );
    setCsrfToken('');
    setUser(null);
    setLoading(false);
    // beginSessionTransition se redefine cada render pero sólo toca refs e
    // imports: capturar la del primer render es correcto y load-bearing (esta
    // identidad DEBE ser estable — la consumen dos handlers de registro único).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    registerSessionExpiredHandler(() => {
      // B-F2 — LA POSPOSICIÓN. Con una sesión de estudio en pantalla NO se toca
      // NADA de lo de abajo: ni la frontera de identidad (que versiona la sesión
      // y purga el screenDataCache), ni la hermana de la bodega (que en un guest
      // PURGA — y la bodega es de donde el examen local está leyendo ahora
      // mismo), ni `setUser(null)`. Se anota la deuda y se sale. La sesión de
      // estudio no puede notar la diferencia porque no hay diferencia que notar.
      if (authExpiryDefer && IS_APP_BUILD) {
        const pantalla = getStudyScreen();
        if (pantalla) {
          // La línea se escribe en la TRANSICIÓN, no en cada disparo: con la
          // sesión muerta el cartero y el despachador van a seguir cobrando 401
          // mientras dure el examen, y una línea por cada uno ahogaría el visor
          // sin añadir un solo dato — la deuda es una, y una sola vez se contrae.
          if (!expiracionPendienteRef.current) {
            expiracionPendienteRef.current = true;
            logAuthDefer(pantalla);
          }
          return;
        }
      }
      aplicarExpiracion();
    });
    return () => registerSessionExpiredHandler(null);
  }, [aplicarExpiracion]);

  // B-F2 — LA OTRA MITAD: el usuario abandonó la pantalla de estudio. Si había
  // una expiración esperando, se cobra AQUÍ, por el mismo camino de siempre —
  // `setUser(null)` y el `ProtectedRoute` haciendo su redirect normal. Entregar
  // el examen NO dispara esto: la pantalla de resultados es la MISMA pantalla
  // montada, así que el usuario sigue adentro hasta que la deja (ver el letrero
  // de lib/studyPresence.js, que es donde se decide qué cuenta como "adentro").
  useEffect(() => {
    if (!IS_APP_BUILD) return undefined;
    registerStudyExitHandler((pantalla) => {
      if (!expiracionPendienteRef.current) return;
      expiracionPendienteRef.current = false;
      logAuthApply(pantalla);
      aplicarExpiracion();
    });
    return () => registerStudyExitHandler(null);
  }, [aplicarExpiracion]);

  // Capture UTM + referrer + landing_path on first visit (first-touch attribution).
  // Persisted in localStorage to survive navigation and signup completion.
  useEffect(() => {
    try {
      const existing = localStorage.getItem('utm_attribution');
      if (existing) return; // first-touch — never overwrite

      // post-t7-1 (F1, #580): la llegada de "Start Free" (`/dashboard?start=1`) guarda la atribucion DEL CLIC (la
      // pagina publica como `landing_path`), no "/dashboard"; lo ya guardado no se pisa (arriba)
      if (!IS_APP_BUILD && esArranqueDeInvitado(`${window.location.pathname}${window.location.search}`)) {
        localStorage.setItem('utm_attribution',
          JSON.stringify(atribucionDelClic(window.location.search, document.referrer || '')));
        return;
      }

      const params = new URLSearchParams(window.location.search);
      const utm = {};
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(k => {
        const v = params.get(k);
        if (v) utm[k] = v.slice(0, 500);
      });

      // Persist only if there's something to attribute (UTMs OR external referrer OR non-root landing)
      const hasAttribution = (
        Object.keys(utm).length > 0 ||
        (document.referrer && !document.referrer.startsWith(window.location.origin)) ||
        window.location.pathname !== '/'
      );

      if (hasAttribution) {
        localStorage.setItem('utm_attribution', JSON.stringify({
          ...utm,
          referrer: document.referrer || null,
          landing_path: window.location.pathname,
          captured_at: new Date().toISOString(),
        }));
      }
    } catch (e) {
      // localStorage may be unavailable (privacy mode etc.) — silent failure is correct
    }
  }, []);

  // Backfill preferred_language server-side (forward-only, best-effort). When the app
  // language is known (detector or an in-app switch) and differs from what's stored on the
  // user, persist it so server-side flows (e.g. the downgrade-notice email) can localize.
  // ~99% of users have no preferred_language yet; this fills it on their next authed load.
  // Never breaks anything: a failed PATCH is swallowed.
  useEffect(() => {
    if (!user || user.is_guest) return;
    let cancelled = false;
    let off = null;
    (async () => {
      try {
        const i18n = (await import('../i18n')).default;
        const persist = async (lng) => {
          const norm = (lng || '').slice(0, 2).toLowerCase();
          if (!['en', 'es'].includes(norm)) return;
          if (norm === user.preferred_language) return;
          try {
            await api.patch('/user/preferences', { preferred_language: norm });
            if (!cancelled) setUser(prev => (prev ? { ...prev, preferred_language: norm } : prev));
          } catch { /* best-effort */ }
        };
        persist(i18n.language);                  // backfill from the currently-known language
        const onLng = (lng) => persist(lng);     // and on any in-app language switch
        i18n.on('languageChanged', onLng);
        off = () => i18n.off('languageChanged', onLng);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; if (off) off(); };
  }, [user]);

  const fetchUser = useCallback(async () => {
    const myVersion = sessionVersion.current;
    // post-t7-2-dinero (B6): el usuario que contesto el servidor, para quien necesite leerlo
    // fresco justo despues (la compra de Apple). Lo que se pinta no cambia.
    let delServidor = null;
    // F6.α: true solo cuando el boot nativo entra (o sigue) en "sin conexión" —
    // en ese caso `loading` NO se apaga en el finally: el arranque no terminó,
    // sigue en pausa detrás de la pantalla offline.
    let stayInOfflineBoot = false;
    // camara-pills (6.3 a): el usuario de la bodega con el que este arranque ya pinto el Panel,
    // o null si no hubo (sin bodega utilizable, la web, o un reintento que ya venia de dentro).
    let deLaBodega = null;
    // F6.8 T5-1 — EL CAMINO DE BODEGA DEL ARRANQUE SIN RED, factorizado.
    //
    // Era el cuerpo del catch del refresh nativo y SIGUE SIÉNDOLO, línea por
    // línea: mismo orden load-bearing (veredicto de bodega ANTES de tocar
    // estado, setUser ANTES de setBootOffline), mismas guardias de
    // sessionVersion, mismo destino. Lo único que cambia es que ahora tiene DOS
    // llamadores: el catch de siempre y la entrada DIRECTA con veredicto DOWN.
    // Devuelve el valor que el llamador asigna a `stayInOfflineBoot` (antes era
    // una asignación in situ; con las guardias no cumplidas el valor era y
    // sigue siendo `false`).
    const enterOfflineBootFromWarehouse = async () => {
      if (sessionVersion.current !== myVersion) return false;
      let offlineUser = null;
      if (!enteredOfflineRef.current) {
        offlineUser = await readOfflineBootUser();
      }
      if (sessionVersion.current !== myVersion) return false;
      if (offlineUser) {
        enteredOfflineRef.current = true;
        setUser(offlineUser);
        setBootOffline(true);
        syncLanguageFromUser(offlineUser);
      } else {
        setBootOffline(true);
      }
      return !enteredOfflineRef.current;
    };
    if (_authDebug()) console.log('[AUTH] Page load. Cookies present:', document.cookie.length > 0);
    try {
      // Refresh tokens on page load to restore CSRF token (POST bypasses CDN cache)
      if (IS_APP_BUILD) {
        // ── Native (bearer) session restore ──
        // The session lives in the token store, not in cookies. With no stored
        // refresh token there is nothing to restore → clean logged-out state
        // (skip the refresh + /me network calls entirely).
        const stored = await getTokens();
        if (!stored?.refresh_token) {
          // TANDA 32 (#297) — ESTE CAMINO TAMBIÉN APAGA LA PAUSA. Era el único
          // de los tres finales de `fetchUser` que no lo hacía (el rechazo de
          // refresh la apaga más abajo, y el refresh exitoso también), y ese
          // hueco es exactamente el atorón que Carlos cazó en el iPhone:
          // cerrar sesión en modo avión deja `bootOffline` encendido —nadie lo
          // apagaba— con `user` ya en null, y el gate de arranque
          // (`routeGuards.js`: bootOffline && !user) pinta la pantalla de "sin
          // conexión" en vez del login. Al volver la red el reintento SÍ corría
          // —el vigía, el intervalo, el foco y el botón, los cuatro—, pero
          // aterrizaba aquí, donde no había nada que apagara la pausa: la
          // pantalla se quedaba puesta para siempre y "Reintentar" no podía
          // cambiar nada. Sólo matar la app lo deshacía, porque el estado nace
          // en `false` en cada proceso.
          //
          // "No hay token que restaurar" es un VEREDICTO, no una espera: la
          // sesión no está en pausa, simplemente no existe. Apagarlo aquí es lo
          // que deja al route guard mandar al login, que es su trabajo.
          if (sessionVersion.current === myVersion) {
            setBootOffline(false);
            setUser(null);
          }
          return; // finally still clears the loading spinner
        }
        // F6.8 T5-1 — CON DICTAMEN DE "SIN SEÑAL", NI SE INTENTA.
        //
        // Hasta hoy este refresh era la PRIMERA petición de red del proceso y
        // salía SIEMPRE: en un arranque en avión se iba condenada hasta el techo
        // de 10 s de api.js, y recién entonces la app entraba a su bodega por el
        // catch de abajo. Con la semilla nativa (T5-1) el veredicto ya dice DOWN
        // en el segundo cero, así que se entra DIRECTO al mismo destino, por el
        // mismo camino factorizado, sin pagar el impuesto.
        //
        // Con UNKNOWN o UP no cambia NADA: el refresh sale exactamente como
        // hoy — y UNKNOWN es lo único que la web puede tener.
        if (isExplicitlyOffline()) {
          stayInOfflineBoot = await enterOfflineBootFromWarehouse();
          return;
        }
        // camara-pills (6.3 a, regla de Carlos del 24-oct): "la app abre directo el Panel, siempre
        // y al instante, tenga o no internet". Con bodega del MISMO dueno, el Panel se pinta YA con
        // el usuario de la bodega (sin el estado de "sin conexion": es un arranque normal) y la
        // sesion se confirma por detras. Antes esto solo pasaba DESPUES de que el refresh fallara,
        // y con la red zombie eso era el splash oscuro hasta el techo de 6 s.
        //
        // POR QUE AQUI NO SALE EL REFRESH EXPLICITO: con el Panel ya montado, sus llamadas (y las
        // del chasis) salen con el token de acceso guardado, que vence a los 15 min; su 401 dispara
        // el refresh del interceptor de `lib/api.js`. Dos refresh con el MISMO refresh token
        // (rotacion) se pisan: el que llega segundo recibe 401 y la sesion se cerraria. Por eso este
        // camino pregunta `/auth/me` y deja que, si hace falta, el refresh lo haga el interceptor:
        // es UNO solo y todas las llamadas hacen fila detras de el. Un 401/403 de ese refresh cierra
        // la sesion como siempre (lo hace el interceptor); un fallo de red, tiempo o 5xx deja la app
        // dentro, en el modo sin conexion de siempre (abajo, en el catch).
        if (!enteredOfflineRef.current) {
          deLaBodega = await readOfflineBootUser();
          if (sessionVersion.current !== myVersion) return;
        }
        if (deLaBodega) {
          enteredOfflineRef.current = true;
          marcarArranquePendiente(true);
          setUser(deLaBodega);
          syncLanguageFromUser(deLaBodega);
          setLoading(false);
        } else {
        try {
          const refreshRes = await api.post('/auth/refresh', withNativeSignal({ refresh_token: stored.refresh_token }), bootTimeout());
          await setTokens(refreshRes.data); // rotation (F3): persist the NEW pair
          if (refreshRes.data.csrf_token) {
            setCsrfToken(refreshRes.data.csrf_token);
          }
        } catch (refreshErr) {
          // F6.α — red ≠ token inválido. Antes este catch era incondicional:
          // arrancar en modo avión ejecutaba clearTokens() y DESTRUÍA una sesión
          // válida del Keychain. Solo un 401/403 real del refresh borra tokens.
          if (isAuthRejection(refreshErr)) {
            // Refresh token dead/expired → drop the store and stay logged out.
            await clearTokens();
            if (sessionVersion.current === myVersion) {
              setBootOffline(false);
              setUser(null);
            }
            return;
          }
          // Pasada final 2 — microscopio: QUÉ forma tuvo el fallo del refresh
          // de arranque (solo registro; el flujo de abajo no cambia).
          logNetError('refresh', refreshErr);
          // Fallo TRANSITORIO (red/timeout/5xx) con tokens presentes: se
          // preservan intactos. F6.4b — boot gate ampliado: si la bodega está
          // poblada y su dueño registrado coincide con el snapshot de la
          // última sesión (readOfflineBootUser valida ambos), la app ENTRA en
          // modo offline: user desde el snapshot, loading termina, las
          // pantallas pintan de bodega. El retry de refresh sigue corriendo
          // de fondo (bootOffline queda true) hasta el veredicto real
          // (éxito → sesión viva sin recargar; 401/403 → rama de arriba).
          // Sin bodega utilizable → pausa "sin conexión" como en F6.α.
          //
          // ORDEN load-bearing (fix del flash, smoke F6.4b): el VEREDICTO de
          // la bodega se obtiene ANTES de tocar cualquier estado. El viejo
          // orden (setBootOffline primero, await después) commiteaba
          // bootOffline=true con user aún null y AuthBootGate pintaba
          // OfflineBootScreen un instante (abrir la conexión cifrada + leer
          // el snapshot no es gratis on-device). Mientras el veredicto se
          // resuelve no cambia NINGÚN estado: loading sigue true y el gate
          // muestra el splash de boot de siempre. Con veredicto:
          //   - bodega válida → setUser ANTES de setBootOffline (React 18
          //     los batchea en un commit; y aun sin batching, con user ya
          //     puesto la condición bootOffline && !user del gate jamás se
          //     cumple) → splash → app, cero frames de la pantalla de retry;
          //   - sin bodega utilizable → setBootOffline(true) con user null →
          //     OfflineBootScreen aparece AHORA, con el veredicto en mano.
          //
          // F6.8 T5-1: el cuerpo de esta rama vive ahora en
          // `enterOfflineBootFromWarehouse` (arriba), sin cambiarle una línea —
          // la entrada directa con veredicto DOWN aterriza en el MISMO estado.
          stayInOfflineBoot = await enterOfflineBootFromWarehouse();
          return;
        }
        // Refresh OK → si veníamos del estado offline, se levanta la pausa.
        if (sessionVersion.current === myVersion) setBootOffline(false);
        }
      } else {
        try {
          if (_authDebug()) console.log('[AUTH] Attempting refresh...');
          const refreshRes = await api.post('/auth/refresh');
          if (refreshRes.data.csrf_token) {
            setCsrfToken(refreshRes.data.csrf_token);
          }
          if (_authDebug()) console.log('[AUTH] Refresh succeeded');
        } catch (refreshErr) {
          if (_authDebug()) {
            const detail = refreshErr.response?.data?.detail;
            const reason = typeof detail === 'object' ? detail.debug_reason : detail;
            console.log('[AUTH] Refresh failed:', reason || refreshErr.message);
          }
          // No valid refresh token — user may still have a valid access token
        }
      }

      const res = await api.get('/auth/me', bootTimeout());
      if (sessionVersion.current === myVersion) {
        if (res.data.csrf_token) {
          setCsrfToken(res.data.csrf_token);
        }
        // F6.4b: sesión viva confirmada — si veníamos de una entrada offline,
        // esta ES la normalización (mismo árbol montado, datos frescos).
        enteredOfflineRef.current = false;
        marcarArranquePendiente(false);
        setUser(res.data);
        delServidor = res.data;
        syncLanguageFromUser(res.data);
        if (_authDebug()) console.log('[AUTH] Session restored for:', res.data.email || res.data.id, 'guest:', res.data.is_guest);
      }
    } catch (meErr) {
      if (deLaBodega) marcarArranquePendiente(false);
      if (sessionVersion.current === myVersion) {
        if (enteredOfflineRef.current && isNetworkShapedError(meErr)) {
          // F6.4b: la red parpadeó entre el refresh y el /me durante una
          // sesión offline activa — NO se expulsa al usuario de su bodega;
          // bootOffline se re-arma para que el retry de fondo siga.
          setBootOffline(true);
        } else if (deLaBodega && !isAuthRejection(meErr)) {
          // camara-pills (6.3 a): el arranque desde la bodega no se pudo confirmar por algo que
          // no es la sesion (un 5xx): se queda dentro, en el modo sin conexion de siempre, con el
          // reintento de fondo. Solo un 401/403 la cierra (rama de abajo; el interceptor ya borro
          // los tokens si su refresh fue rechazado).
          setBootOffline(true);
        } else {
          enteredOfflineRef.current = false;
          setUser(null);
        }
      }
      if (_authDebug()) console.log('[AUTH] /me failed, no active session');
    } finally {
      // ALWAYS stop loading — never leave the spinner stuck.
      // Only setUser is gated by sessionVersion; loading is always safe to clear.
      // Única excepción (F6.α): el boot offline nativo NO terminó — loading sigue
      // true para que los guards no naveguen; la pantalla offline (con botón
      // Reintentar) cubre el hueco, así que no hay spinner colgado.
      if (!stayInOfflineBoot) setLoading(false);
    }
    return delServidor;
  }, []);

  /* post-t7-2-dinero (B6, firma 3): en la app, al volver al frente, se vuelve a preguntar el plan
     (como mucho una vez por minuto), para que quien acaba de pagar en la web no vea "Upgrade" y
     compre otra vez por Apple. Solo con usuario registrado. En la web nada cambia. */
  const ultimaRelecturaRef = useRef(0);
  const registradoParaRelectura = Boolean(user && !user.is_guest);
  useEffect(() => {
    if (!IS_APP_BUILD || !registradoParaRelectura) return undefined;
    const alVolver = () => {
      if (document.visibilityState !== 'visible') return;
      const ahora = Date.now();
      if (ahora - ultimaRelecturaRef.current < RELECTURA_AL_VOLVER_MS) return;
      ultimaRelecturaRef.current = ahora;
      fetchUser();
    };
    document.addEventListener('visibilitychange', alVolver);
    return () => document.removeEventListener('visibilitychange', alVolver);
  }, [registradoParaRelectura, fetchUser]);

  // F6.α — reintento del boot "sin conexión". Serializado con un ref: si un
  // intento sigue en vuelo (p. ej. colgado en el timeout de red), ni el interval
  // ni el botón apilan otro encima.
  const retryBootRefresh = useCallback(async () => {
    if (bootRetryInFlight.current) return;
    // F6.8 T5-1 (aditivo, AHORRO DE RADIO): con dictamen de "sin señal" el
    // intento está condenado antes de salir, así que no sale. Esto NO retrasa
    // la vuelta: el reintento instantáneo al recuperar señal ya existía por
    // S-4 (la suscripción de abajo, que dispara con UP) y desde la T5-2 el
    // plugin nativo también escribe ese UP. Con UNKNOWN o UP —los únicos
    // estados posibles en web— el reintento sale igual que hoy.
    //
    // REMATE-266 — ESTA LÍNEA YA ESTABA, Y HASTA HOY NO SERVÍA DE NADA BAJO RED
    // ZOMBIE. El recon del congelamiento la señaló como el freno que faltaba
    // ("el intervalo de 4 s martilla la red para siempre"), pero el freno existe
    // desde la T5-1: lo que no existía era la manera de que el veredicto LLEGARA
    // a DOWN con la interfaz viva y el internet muerto. Eso lo pone la evidencia
    // hacia abajo de esta misma tanda (`markNetworkEvidenceDown`), así que desde
    // aquí no hubo nada que cambiar: con dos peticiones muertas el veredicto
    // baja, y este `return` convierte el intervalo de 4 s en un no-op que ni
    // toca la radio hasta que la señal vuelva de verdad.
    if (isExplicitlyOffline()) return;
    bootRetryInFlight.current = true;
    try {
      await fetchUser();
    } finally {
      bootRetryInFlight.current = false;
    }
  }, [fetchUser]);

  // Mientras el boot esté en "sin conexión": reintenta solo cada BOOT_RETRY_MS y
  // además al recuperar foco/visibilidad (volver de Settings tras quitar el modo
  // avión es el caso típico). Todo se desarma al salir del estado.
  //
  // F6.7b (2ª pasada) — SE DESTRABA CON EL VIGÍA, no con su reloj. El arranque
  // en pausa ahora escucha el MISMO dictamen que el resto de la app: en cuanto
  // llega `online` reintenta AL INSTANTE, en vez de esperar hasta 4 s a que le
  // toque su turno al intervalo. Eso es lo que hace que el usuario quite el
  // modo avión y la app entre sola, sin tocar nada.
  //
  // EL INTERVALO SE QUEDA, y la semántica es la razón (no la costumbre):
  // "sesión sin refrescar" ≠ "sin red". A este estado también se llega con red
  // perfecta — un 5xx del backend, un timeout, un DNS que tarda — y en esos
  // casos NUNCA va a llegar un evento `online` (nunca hubo un `offline`). Sin
  // el reloj, esos arranques se quedarían esperando un aviso que no existe.
  // El vigía ACELERA el caso de red; el intervalo cubre todos los demás.
  useEffect(() => {
    if (!bootOffline) return undefined;
    const intervalId = setInterval(retryBootRefresh, BOOT_RETRY_MS);
    const onFocus = () => { retryBootRefresh(); };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') retryBootRefresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    const unsubscribeNet = subscribeNetworkVerdict((verdict) => {
      if (verdict === NET_VERDICT.UP) retryBootRefresh();
    });
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      unsubscribeNet();
    };
  }, [bootOffline, retryBootRefresh]);

  // F6.4b — disparadores del downloader (bajada silenciosa de la bodega):
  // (a)/(c) al poblarse una identidad autenticada — el boot con red, login,
  // guest, verificación, OAuth y conversión terminan todos en setUser, así
  // que un solo effect keyed por user.id cubre los tres disparadores del
  // sprint sin tocar cada call site; corre DESPUÉS del primer render
  // (no-bloqueante, patrón anti-aditivo del splash F4.3b) — y (b) el evento
  // studybuddy:docs-changed (un doc terminó de procesar → hay bundle nuevo).
  // Import dinámico gateado por IS_APP_BUILD: chunk que la web jamás pide
  // (patrón tokenStore). El downloader se auto-serializa y en un arranque
  // offline falla en silencio (reintenta en el próximo disparador).
  // F6.7a: los dos disparadores de aquí pasan ahora por la convergencia, pero
  // con motivo `boot`/`docs-changed` — los ÚNICOS que despiertan al cartero
  // SIEMPRE, sin consultar el plan de huecos. El comportamiento de este effect
  // queda idéntico al de hoy a propósito: `syncWarehouseDown` no solo baja
  // bundles (committea la foto de progreso, mergea posiciones, re-pliega el
  // diario, borra docs muertos), así que gatearlo por "¿hay material nuevo?"
  // habría dejado de sincronizar progreso en las corridas sin docs nuevos.
  const downloaderUserId = user?.id;
  useEffect(() => {
    if (!IS_APP_BUILD || !downloaderUserId) return undefined;
    /* TANDA 19 (#211) — Y CON EL MISMO DISPARADOR, LA ÚLTIMA FOTO AL CACHE.
       Va aquí y no dentro del ciclo de abajo por dos razones: arranca EN EL
       MISMO TICK (una lectura local de SQLite no tiene por qué esperar al
       despacho ni a la convergencia) y no comparte su suerte —esta línea no
       puede retrasar ni romper nada, y el `catch` es lo único que hace falta
       para garantizarlo—. Se re-dispara con `docs-changed` igual que su
       vecina, que es lo que deja el `Map` repoblado antes de que el usuario
       vuelva de una sub-ruta. Chunk dinámico: la web nunca lo pide.

       TANDA 22 (#214): la línea salió del cuerpo de `fire` a esta función con
       nombre porque ahora tiene DOS llamadores. No cambió ni un carácter de lo
       que hace, ni cuándo lo hace dentro de `fire`: sigue siendo lo primero del
       tick, antes del despacho y de la convergencia. */
    const hidratar = () => {
      import('../lib/warehouse/hidratarCache').then((h) => h.hidratarCacheDeBodega(downloaderUserId)).catch(() => {});
    };
    const fire = (evt) => {
      hidratar();
      // F6.5b — orden del ciclo (D-K / Tarea 4): SUBIR pendientes del diario
      // (despachador, solo post-corte — pre-corte es no-op por el switch) →
      // BAJAR foto (downloader; el re-fold de pendientes viaja dentro de la
      // misma transacción de la foto). Ambos contienen todos sus errores.
      (async () => {
        if (anyModeRunsLocally()) {
          try {
            const d = await import('../lib/brain/dispatcher');
            await d.dispatchPending();
          } catch (e) { /* el despacho jamás rompe nada */ }
        }
        const c = await import('../lib/warehouse/converge');
        await c.convergeWarehouse(
          evt && evt.type ? c.CONVERGE_REASON.DOCS_CHANGED : c.CONVERGE_REASON.BOOT
        );
      })().catch(() => {});
    };
    fire();
    window.addEventListener('studybuddy:docs-changed', fire);
    /* ── TANDA 22 (#214) — EL TERCER DISPARADOR: CERRAR UNA SESIÓN DE ESTUDIO ──
       EL HUECO QUE CIERRA. La hidratación de la T19 nombra el retorno de una
       sub-ruta como su caso caro y se apoya en `docs-changed` para llegar a
       tiempo. Esa premisa es falsa justo aquí: `docs-changed` sólo lo emite el
       sondeo cuando un documento pasa de procesando a terminal, y terminar una
       sesión de estudio no mueve el estado de ningún documento. Peor: el cierre
       SÍ borra la llave del Centro de Estudio (el cache escucha este mismo
       evento), así que el carril se remontaba con la llave vacía y sin nadie que
       la volviera a llenar.
       QUÉ SE AGREGA, Y QUÉ NO. Sólo la lectura local de bodega, con el MISMO id
       de dueño y el MISMO cleanup por identidad. NO entra por `fire`: eso
       despertaría además al cartero en cada cierre de sesión, que es red nueva y
       no es lo que esta firma pide.
       LA CARRERA NO SE APUESTA. El evento se emite todavía dentro de la sesión;
       la invalidación del cache es síncrona y corre primero, y esta escritura
       llega después porque leer la bodega es asíncrono. Si la foto le gana al
       remonte del carril, la pane nace con su semilla y pinta la lista de una
       vez ("pinta Y pregunta", T19-f2); si la pierde, la vacuna de `StudyCenter`
       pinta silueta y el fetch trae la lista. Los dos finales son correctos, y
       por eso ninguna prueba exige ganarla. */
    window.addEventListener(SESSION_COMMITTED_EVENT, hidratar);
    return () => {
      window.removeEventListener('studybuddy:docs-changed', fire);
      window.removeEventListener(SESSION_COMMITTED_EVENT, hidratar);
    };
  }, [downloaderUserId]);

  // AS-3 — EL CARTERO DE COMPRAS. Hermano exacto del disparador del downloader de arriba:
  // app-only, import dinámico (chunk que la web jamás pide) y todos sus errores contenidos.
  //
  // VA SIN `user` EN LAS DEPS A PROPÓSITO. El listener de StoreKit tiene que estar puesto
  // AUNQUE NO HAYA SESIÓN: si una transacción pagada llega con la sesión cerrada, el
  // plugin la finaliza al entregarla (lo hace ANTES de avisarnos, ver lib/purchases.js) y
  // StoreKit no la volverá a entregar jamás. Con el listener puesto, el recibo se guarda
  // en la cola durable y se cobra en el siguiente login. La VERIFICACIÓN sí exige sesión,
  // y de eso se encarga el effect de abajo.
  useEffect(() => {
    if (!IS_APP_BUILD) return undefined;
    let off = null;
    let vivo = true;
    import('../lib/purchases')
      .then(async (m) => {
        const parar = await m.startPurchaseCourier({ onApplied: fetchUser });
        if (vivo) off = parar; else parar();
      })
      .catch(() => {});
    return () => { vivo = false; if (off) off(); };
  }, [fetchUser]);

  // AS-3 — la otra mitad: con una identidad autenticada viva, lo que quedó encolado se
  // cobra ahora. Cubre el caso "compró sin sesión (o con el backend caído) y volvió".
  useEffect(() => {
    if (!IS_APP_BUILD || !downloaderUserId) return;
    import('../lib/purchases')
      .then((m) => {
        // La identidad viva, para el `appAccountToken` de las compras (así ninguna
        // superficie de compra tiene que colgarse del contexto de auth por esto).
        m.setPurchaseUser(downloaderUserId);
        return m.flushPendingVerifications();
      })
      .catch(() => {});
  }, [downloaderUserId]);

  // RPW T7 (P2 / D-G2a) — AQUÍ COLGABA EL BARRENDERO DE BOOT DEL EXAMEN (F6.6b,
  // D-N): un effect propio, keyed por la MISMA frontera de identidad del cerebro
  // (`downloaderUserId`), que llamaba a `examBootTasks()` del runtime.
  //
  // Se retira con el barrendero, que ya no existe. Su última rama viva era la
  // del INVITADO —examen en vuelo sin proceso vivo → `abandoned`— y esa rama es
  // el hallazgo S11 del smoke: matar la app le regalaba un examen fresco sin
  // red, infinitas veces. Bajo la regla firmada (D-G2a) el examen del invitado
  // SOBREVIVE al kill igual que el del registrado, así que el boot no tiene nada
  // que barrer: al entrar al doc, la resolución de la pantalla cae dentro del
  // mismo examen (regla 0) o lo auto-entrega si su hora límite ya pasó.
  //
  // Con él se va el ÚNICO consumidor de `examRuntime` fuera de la pantalla del
  // examen: este archivo ya no pide ese chunk por ningún camino.

  // CARRIL-DIAG — EL TRIPWIRE DEL CARRIL. Observador EXTERNO y de SOLO LECTURA:
  // vigila la ley H-1 (sin transform, `left` en múltiplo exacto de −100%,
  // `scrollLeft` en cero) y escribe `NAV.H1` en el visor cuando la encuentra rota.
  // Cero cambios a los archivos del carril y de gestos, cero auto-corrección.
  // Mismo molde que el cartero de arriba: app-only, import dinámico (chunk que la
  // web jamás pide) y todos sus errores contenidos. Sin `user` en las deps: la
  // geometría del carril no depende de quién esté firmado.
  useEffect(() => {
    if (!IS_APP_BUILD) return undefined;
    let parar = null;
    let vivo = true;
    import('../lib/diag/carrilTripwire')
      .then((m) => {
        const off = m.arrancarCarrilTripwire();
        if (vivo) parar = off; else off();
      })
      .catch(() => {});
    return () => { vivo = false; if (parar) parar(); };
  }, []);

  // F6.5a — warm-up del cerebro (Pyodide en worker) DURANTE el splash: effect
  // hermano del downloader, gateado SOLO por IS_APP_BUILD (no necesita user —
  // el motor es idéntico para todas las identidades y arrancarlo en el primer
  // render es lo que mete su carga de ~1.1-1.6 s bajo el dwell de 1.4 s del
  // splash, concurrente). El splash JAMÁS espera esta promesa (patrón
  // anti-aditivo F4.3b): `loading` se apaga con fetchUser, y si el worker
  // tarda más que el dwell, sigue calentando de fondo. Import dinámico:
  // chunk que la web jamás pide (patrón tokenStore) y que Jest jamás parsea
  // (engineHost contiene `import.meta`). Un warm-up fallido se traga: el
  // siguiente consumidor del host reintenta (warmUp no envenena reintentos).
  useEffect(() => {
    if (!IS_APP_BUILD) return;
    import('../lib/brain/engineHost')
      .then((m) => m.warmUp())
      .catch(() => {});
  }, []);

  // F6.4b — snapshot del user para el boot offline: se refresca con cada
  // user vigente de una sesión nativa (boot online, login, y cambios de
  // perfil/plan vía refreshUser). La guardia de dueño vive en
  // writeUserSnapshot (jamás escribe sobre la bodega de otro). Para la
  // sesión ENTRADA offline es un no-op semántico: el snapshot ES el user.
  useEffect(() => {
    if (!IS_APP_BUILD || !user) return;
    writeUserSnapshot(user);
  }, [user]);

  // COS-22-R — "en este aparato ya se inició sesión alguna vez" (ver
  // lib/returningUser.js). La lee routeGuards para decidir si el destino sin
  // sesión es /signup (instalación nueva) o /login (usuario que ya entró).
  //
  // UN SOLO SITIO, A PROPÓSITO: cualquier `user` no-invitado vigente la
  // enciende, así que quedan cubiertos TODOS los caminos de entrada sin
  // repartir la llamada por seis funciones — login, verifyEmail (la pata que
  // emite sesión del registro), OAuth, conversión de invitado, y también la
  // restauración de sesión al arrancar. Esto último es lo que cubre a quien ya
  // tenía la app instalada antes de este cambio: su primer arranque con sesión
  // viva enciende la marca sin obligarlo a volver a entrar.
  //
  // INVITADOS NO. Un invitado no tiene credenciales con las cuales "volver a
  // entrar": mandarlo a /login tras su sesión sería un callejón sin salida. La
  // firma de Carlos dice "si ya has iniciado sesión ... desde tu cuenta", y un
  // invitado no tiene cuenta — su destino correcto sigue siendo /signup.
  //
  // Gateado por IS_APP_BUILD: la web no consulta la marca y no cambia en nada.
  useEffect(() => {
    if (!IS_APP_BUILD || !user || user.is_guest) return;
    markSignedInBefore();
  }, [user]);
  // post-t7-1 (J1, #564): la marca hermana, en el mismo sitio: en la app, en cuanto se ve un invitado, este telefono
  // ya no vuelve a ver la bienvenida (la marca nunca se apaga, tampoco al cerrar sesion).
  useEffect(() => {
    if (!IS_APP_BUILD || !user || !user.is_guest) return;
    markGuestBefore();
  }, [user]);

  // NOTIFICACIONES-1 (firma #464) — EL PUSH ARRANCA CON CADA SESIÓN VIGENTE. Mismo
  // criterio que la marca de arriba: un solo sitio cubre el arranque con sesión y TODOS
  // los caminos de entrada (y el cambio de cuenta, porque cambia el id). Aquí SÍ entran
  // los invitados: también suben documentos y también esperan a que terminen. Sin
  // permiso concedido no pregunta nada (eso lo hace la primera subida).
  useEffect(() => {
    if (!IS_APP_BUILD || !user?.id) return;
    arrancarPush();
  }, [user?.id]);

  useEffect(() => {
    // Google OAuth return leg: don't fetch /me while AuthCallback is exchanging the
    // code — it would race the callback and stomp the fresh session (same purpose as
    // the old hash-based skip).
    // AS-4: el retorno de Apple (/auth/apple/callback) necesita el MISMO salto por la
    // misma razón — su página hermana está entregando el id_token al backend.
    if (window.location.pathname === '/auth/google/callback'
        || window.location.pathname === '/auth/apple/callback') {
      setLoading(false);
      return;
    }
    fetchUser();
  }, [fetchUser]);

  const login = async (email, password) => {
    beginSessionTransition();
    setCsrfToken(''); // Clear stale CSRF before login attempt
    const res = await api.post('/auth/login', withNativeSignal({ email, password }));
    if (IS_APP_BUILD) await setTokens(res.data); // native: persist the bearer pair
    // Frontera hermana de la bodega (F6.4a): mismo dueño conserva, distinto purga.
    // Va DESPUÉS del post porque compara contra el id que acaba de entrar.
    await syncWarehouseIdentity(WAREHOUSE_BOUNDARY.LOGIN, res.data.user.id);
    setCsrfToken(res.data.csrf_token);
    setUser(res.data.user);
    setLoading(false);
    identifyUser(res.data.user.id, { email, plan: res.data.user.plan, name: res.data.user.name });
    trackEvent('user_logged_in', { plan: res.data.user.plan });
    syncLanguageFromUser(res.data.user);
    return res.data;
  };

  const signup = async (email, password, name, turnstile_token = '') => {
    // /auth/signup issues NO session (Part 1: creates a pending_verification and
    // sends the code). The bearer pair for the register flow arrives at
    // /auth/verify (see verifyEmail). The native signal is carried here too for a
    // consistent flow origin; the backend ignores it on this endpoint.
    const res = await api.post('/auth/signup', withNativeSignal({ email, password, name, turnstile_token }));
    // Part 2: No user created yet — don't set user state or CSRF
    return res.data;
  };

  const loginAsGuest = async (opciones) => {
    if (_authDebug()) console.log('[GUEST-LOGIN] loginAsGuest() called. Current user:', user || 'null');
    beginSessionTransition();
    // post-t7-1 (F1, #580): "Start Free" manda la atribucion del clic; sin ella, el cuerpo de siempre
    const atribucion = opciones && opciones.atribucion;
    const res = await api.post('/auth/guest', withNativeSignal(atribucion ? { attribution: atribucion } : undefined));
    if (IS_APP_BUILD) await setTokens(res.data); // native: persist the bearer pair
    // Frontera hermana de la bodega (F6.4a): un guest nuevo casi siempre es
    // dueño distinto → purga (mismo criterio comparado que login).
    await syncWarehouseIdentity(WAREHOUSE_BOUNDARY.GUEST_CREATED, res.data.user.id);
    setCsrfToken(res.data.csrf_token);
    setUser(res.data.user);
    setLoading(false);
    trackEvent('guest_created');
    syncLanguageFromUser(res.data.user);
    return res.data;
  };

  const verifyEmail = async (email, code) => {
    beginSessionTransition();
    // /auth/verify is the token-issuing leg of the register flow: on success the
    // backend creates the user and issues the session, returning the bearer pair
    // in native mode (auth.py:430 _deliver_session). This is where the store gets
    // seeded for a native signup.
    const res = await api.post('/auth/verify', withNativeSignal({ email, code }));
    if (IS_APP_BUILD) await setTokens(res.data); // native: persist the bearer pair
    // Frontera hermana de la bodega (F6.4a): mismo criterio comparado que login.
    await syncWarehouseIdentity(WAREHOUSE_BOUNDARY.EMAIL_VERIFIED, res.data.user.id);
    if (res.data.csrf_token) {
      setCsrfToken(res.data.csrf_token);
    }
    setUser(res.data.user);
    setLoading(false);
    identifyUser(res.data.user.id, { email: res.data.user.email, plan: res.data.user.plan, name: res.data.user.name });
    trackEvent('email_verified');
    if (window.rdt) window.rdt('track', 'SignUp');
    syncLanguageFromUser(res.data.user);
    return res.data;
  };

  const resendVerification = async (email) => {
    const res = await api.post('/auth/resend-verification', { email });
    return res.data;
  };

  const logout = async (opts = {}) => {
    // ajustes-1-3-0 (Bloque H2, #507): quien cierra se decide AQUI, antes de que
    // `setUser(null)` borre al usuario del estado; la frontera de la bodega lo usa abajo.
    const eraInvitado = !!(user?.is_guest || lastUserRef.current?.is_guest);
    // D-D (F6.5b): guardia de renglones sin sincronizar. ANTES de cualquier
    // purga: si hay respuestas locales pendientes de subir, se intenta el
    // despacho (timeout corto); si aun así quedan, aviso honesto — confirmar
    // sigue con la purga (flujo actual), cancelar CONSERVA la sesión entera
    // (cero efectos: ni frontera, ni purga, ni revocación). `force: true`
    // la omite (borrado de cuenta: la cuenta ya no existe server-side, los
    // renglones no tienen a dónde subir). En web y pre-corte es inerte:
    // el diario no existe / está vacío, el conteo da 0 y el flujo actual
    // queda byte-idéntico. La guardia jamás BLOQUEA un logout: cualquier
    // error propio cuenta como "nada pendiente".
    if (IS_APP_BUILD && opts.force !== true) {
      try {
        const d = await import('../lib/brain/dispatcher');
        const remaining = await d.flushBeforeLogout();
        if (remaining > 0) {
          const i18n = (await import('../i18n')).default;
          const proceed = window.confirm(
            i18n.t('common:logout_guard.unsynced_warning', { count: remaining })
          );
          if (!proceed) return { cancelled: true };
        }
      } catch (e) { /* la guardia nunca convierte un logout en error */ }
    }
    // F6.7b (2ª pasada) — EL LOGOUT NO PUEDE ENVENENAR AL SIGUIENTE LOGIN.
    //
    // El bug del smoke (28-jul): tras cerrar sesión, iniciar sesión fallaba
    // "sin razón" y solo se destrababa matando la app o esperando varios
    // segundos. La cola de este método lo explica y el registro lo confirma
    // (DL.STATE http:401, DL.BUNDLE 401 después del logout). Dos venenos:
    //
    //   1. TRÁFICO ZOMBI. El cartero y el despachador pueden tener peticiones
    //      EN VUELO cuando el usuario cierra sesión. Al revocarse la sesión,
    //      cada una vuelve 401 → el interceptor global intenta refrescar → si
    //      el refresh sale con el token ya borrado, eso es un rechazo de
    //      sesión: clearTokens() + _onSessionExpired(). Si ESO aterriza sobre
    //      un login recién hecho, borra la sesión nueva y expulsa al usuario.
    //      Se corta de raíz: las peticiones de la sesión que muere se ABORTAN
    //      aquí (api.js las clasifica como forma-de-red, así que nadie
    //      confunde el aborto con una sesión inválida).
    //
    //   2. LA COLA LENTA DEL LOGOUT. `setUser(null)` pinta la pantalla de
    //      login de inmediato, pero debajo seguían corriendo la purga de la
    //      bodega (plugins nativos, varios segundos) y el POST de revocación
    //      (hasta el techo de 10 s) ANTES de `clearTokens()`. Un login hecho
    //      en esa ventana escribía sus tokens nuevos y la cola del logout los
    //      borraba después: sesión "iniciada" sin credenciales, que es
    //      exactamente "no me deja entrar hasta matar la app". Ahora la
    //      sesión local se cierra ANTES de lo lento (el token de revocación se
    //      lee antes de borrarlo, así el server sigue enterándose), y todo lo
    //      que toca estado global va con guardia de versión: si empezó otra
    //      sesión, esta cola ya no le pertenece y no la toca.
    // NOTIFICACIONES-1 (firma #464) — LA BAJA DEL TOKEN DEL PUSH, ANTES DE BORRAR LA
    // SESIÓN: la pide esta misma sesión, así que va antes de la transición (que aborta lo
    // que esté en vuelo) y antes de `clearTokens()`. Con techo propio de 3 s y sin poder
    // fallar: una baja que no llega deja el token con la cuenta que sale hasta que otra
    // sesión lo registre en este teléfono (el registro lo mueve de cuenta).
    if (IS_APP_BUILD) {
      try { await darDeBajaPush(); } catch (e) { /* la baja jamás convierte un logout en error */ }
    }
    const revokeToken = IS_APP_BUILD ? (await getTokens())?.refresh_token : undefined;
    beginSessionTransition();
    const myVersion = sessionVersion.current;
    // Expulsión INMEDIATA (regla de fronteras: ningún frame de la sesión
    // anterior tras una transición). user→null en este mismo tick hace que el
    // route guard pinte signup/login en el siguiente render. Cuando setUser
    // vivía al final, la navegación del caller (Sidebar navega a '/' → en app
    // redirige a /dashboard) ocurría con el usuario aún puesto: el Dashboard
    // alcanzaba a montar (skeleton → contenido) mientras las purgas seguían en
    // vuelo — el flash post-logout del smoke on-device F6.4a. Las purgas de
    // abajo continúan COMPLETAS y en su orden intacto (bodega → revocación):
    // este async no depende del árbol montado, así que la navegación no las
    // interrumpe.
    //
    // TANDA 32 (#297) — Y LA PAUSA DE ARRANQUE SE APAGA EN EL MISMO TICK.
    // Cerrar sesión NO es "el arranque sigue esperando red": es un veredicto
    // pedido por el usuario. Con la pausa encendida —lo normal cuando se estudia
    // sin señal: la app entra por bodega y la deja prendida como motor del
    // reintento de fondo— este `setUser(null)` hacía verdadera la condición del
    // gate de arranque (bootOffline && !user) y el logout aterrizaba en la
    // pantalla de "sin conexión" en lugar del login.
    //
    // Los dos apagadores de esta tanda son CINTURÓN Y TIRANTES a propósito: éste
    // impide que la pantalla llegue a aparecer, y el de `fetchUser` (arriba)
    // impide que se quede pegada si llegara por cualquier otro camino. Cada uno
    // arregla su mitad y ninguno depende del otro.
    setBootOffline(false);
    // post-t7-1 (E1): cerrar sesion borra la ruta que el guardia guardo (la siguiente persona empieza en el Panel)
    // post-t7-1 N (revisor de puertas): y marca la ruta en la que estaba, para que el guardia que pinta justo despues
    // (en la app nadie navega al cerrar sesion) no la vuelva a guardar
    marcarSalida(`${window.location.pathname || ''}${window.location.search || ''}`);
    borrarRutaPendiente();
    setUser(null);
    // Solo en app, y a propósito: el tráfico zombi que envenena es el del
    // cartero y el despachador (nativos, con bearer). En web la sesión vive en
    // cookies httpOnly, no hay bodega ni descargas de fondo, y cancelar
    // lecturas en vuelo solo cambiaría —sin arreglar nada— el comportamiento de
    // una superficie que hoy funciona.
    if (IS_APP_BUILD) abortAllInFlight();
    // PILOTO-1C — EL CÓDIGO DE GRUPO PENDIENTE MUERE CON LA SESIÓN.
    //
    // Era del NAVEGADOR y sobrevivía al logout (diseño de PILOTO-1), así que en
    // una máquina compartida —el laboratorio de la escuela, que es el caso del
    // piloto— el siguiente usuario heredaba el empujón del anterior: un modal
    // ofreciéndole entrar a un grupo que no es suyo. Quedó declarado como
    // pendiente en reporte_piloto1b.md §7 y se cierra aquí.
    //
    // UN SOLO PUNTO DE LIMPIEZA, y es éste. No va en `beginSessionTransition`
    // —que también corre en el LOGIN— porque ahí borraría justo el código que el
    // alumno acaba de guardar en `/join/CODIGO` antes de entrar: sería matar la
    // función entera para arreglar su caso raro. Cerrar sesión es la única
    // frontera que significa «no dejes nada mío en este aparato».
    olvidarEmpujonPendiente();
    // TANDA 32 (#297) — CERRAR SESIÓN SIN RED SE PERMITE, Y SE DICE.
    //
    // Ya se permitía —el POST de revocación de abajo vive en un `try/catch` que
    // ignora el fallo de red desde siempre, y todo lo local se limpia igual—,
    // pero el usuario se quedaba sin saber la mitad importante: sus
    // credenciales acaban de morir en este aparato y volver a entrar necesita
    // internet. Sin el aviso, quien cierra sesión en el avión descubre eso al
    // intentar entrar, que es el peor momento posible.
    //
    // SÓLO SIN SEÑAL, y con DICTAMEN explícito del vigía: con red este toast
    // sería ruido sobre un logout que se comporta como siempre. `isExplicitly
    // Offline` sólo puede ser cierto en la app (en web el veredicto nunca baja
    // de UNKNOWN), así que la web queda byte-idéntica.
    //
    // NO BLOQUEA NI PUEDE ROMPER EL LOGOUT: import dinámico dentro de su propio
    // `try/catch` y sin `await` sobre el toast — el mismo trato que la guardia
    // de pendientes de arriba se da a sí misma. Un toast que reventara y se
    // llevara el cierre de sesión sería mucho peor que un toast que no sale.
    if (isExplicitlyOffline()) {
      try {
        const [{ toast }, i18nMod] = await Promise.all([
          import('sonner'), import('../i18n'),
        ]);
        toast.info(i18nMod.default.t('common:logout_offline_notice'));
      } catch (e) { /* el aviso jamás convierte un logout en error */ }
    }
    // Native: la sesión local se cierra YA (ver veneno 2). El usuario pidió
    // salir: sus credenciales locales mueren antes que cualquier cosa lenta.
    // El CSRF (web) se limpia al final como siempre — allá la sesión vive en
    // cookies httpOnly que solo el server puede borrar, así que adelantarlo no
    // arreglaría nada y sí cambiaría el orden de un flujo que hoy funciona.
    if (IS_APP_BUILD) await clearTokens();
    // Frontera hermana de la bodega (F6.4a): logout (incluye borrado de
    // cuenta) purga TODO siempre, sin comparar — cerrar sesión en este
    // dispositivo es la petición explícita de no dejar nada local.
    // ajustes-1-3-0 (Bloque H2, #507): un registrado CONSERVA su bodega (LOGOUT ya no purga);
    // el invitado y la cuenta borrada (`opts.cuentaBorrada`, DeleteAccountModal) la pierden.
    // El orden de darDeBajaPush, beginSessionTransition, setUser(null), clearTokens y el POST
    // de logout no se mueve: solo cambia QUE frontera se manda.
    await syncWarehouseIdentity(
      opts.cuentaBorrada === true ? WAREHOUSE_BOUNDARY.ACCOUNT_DELETED
        : eraInvitado ? WAREHOUSE_BOUNDARY.LOGOUT_GUEST
          : WAREHOUSE_BOUNDARY.LOGOUT,
    );
    try {
      // Native: revoke server-side by sending the stored refresh token in the body.
      const body = IS_APP_BUILD ? withNativeSignal({ refresh_token: revokeToken }) : undefined;
      await api.post('/auth/logout', body);
    } catch {
      // Still clear local state even if API call fails
    }
    // Guardia de versión: si mientras esta cola corría empezó otra sesión
    // (login inmediato), lo que sigue ya no es de esta transición — y
    // `setCsrfToken('')` sobre la sesión nueva la dejaría sin CSRF en web.
    if (sessionVersion.current !== myVersion) return;
    setCsrfToken('');
    trackEvent('user_logged_out');
    resetAnalytics();
  };

  const handleOAuthCallback = async (code, redirectUri) => {
    beginSessionTransition();
    const res = await api.post('/auth/google-callback', { code, redirect_uri: redirectUri });
    // Frontera hermana de la bodega (F6.4a): mismo criterio comparado que login.
    await syncWarehouseIdentity(WAREHOUSE_BOUNDARY.OAUTH_CALLBACK, res.data.user.id);
    setCsrfToken(res.data.csrf_token);
    setUser(res.data.user);
    setLoading(false);
    syncLanguageFromUser(res.data.user);
    return res.data;
  };

  // AS-4 — hermano de handleOAuthCallback para Sign in with Apple (web). Misma
  // semántica de frontera de bodega (OAUTH_CALLBACK: purga si el dueño cambió) porque
  // es exactamente el mismo evento — una sesión que entra por un proveedor externo.
  const handleAppleCallback = async (idToken, nonce, userPayload, code) => {
    beginSessionTransition();
    const body = { id_token: idToken, nonce };
    if (userPayload) body.user = userPayload; // solo en la PRIMERA autorización
    // AS-6a: el authorization code viaja OPCIONAL. Un backend viejo lo ignora y un
    // frontend viejo no lo manda: la ventana de despliegue aguanta en los dos sentidos.
    if (code) body.code = code;
    const res = await api.post('/auth/apple-callback', body);
    await syncWarehouseIdentity(WAREHOUSE_BOUNDARY.OAUTH_CALLBACK, res.data.user.id);
    setCsrfToken(res.data.csrf_token);
    setUser(res.data.user);
    setLoading(false);
    syncLanguageFromUser(res.data.user);
    return res.data;
  };

  // AS-5 — la llegada de los dos logins NATIVOS. Hermana de handleOAuthCallback: recibe
  // la respuesta de sesión que ya trajo `lib/nativeSocialLogin` (el POST lo hace ese
  // módulo, que es el único que habla con el plugin) y la asienta en el contexto.
  //
  // Tres diferencias contra el hermano web, y las tres son del mundo nativo:
  //   · `setTokens` SIEMPRE — este camino solo existe en el build de app, donde la sesión
  //     vive en el Keychain y no en cookies (que mueren en capacitor://localhost);
  //   · sin salto del `/me`: ese salto es una comparación de `window.location.pathname`
  //     contra las dos páginas de retorno, y aquí no hay retorno ni navegación — el
  //     provider ya montó y ya hizo su `fetchUser`. Lo que evita la carrera es
  //     `beginSessionTransition()`, que invalida cualquier lectura en vuelo;
  //   · la misma cola de un login nativo por correo (identifyUser + trackEvent +
  //     idioma), porque para todo lo de después esto ES un login nativo.
  //
  // La frontera de bodega es OAUTH_CALLBACK, igual que sus dos hermanos web: es
  // exactamente el mismo evento — una sesión que entra por un proveedor externo — y
  // purga si el dueño cambió.
  const handleNativeSocialLogin = async (data, provider) => {
    beginSessionTransition();
    await setTokens(data);
    await syncWarehouseIdentity(WAREHOUSE_BOUNDARY.OAUTH_CALLBACK, data.user.id);
    setCsrfToken(data.csrf_token);
    setUser(data.user);
    setLoading(false);
    identifyUser(data.user.id, { email: data.user.email, plan: data.user.plan, name: data.user.name });
    trackEvent(provider === 'apple' ? 'user_logged_in_apple' : 'user_logged_in_google', { plan: data.user.plan });
    // Reddit Pixel SignUp: mismo criterio que las dos páginas de retorno web — solo
    // cuando el backend reporta cuenta recién creada.
    if (data.is_new_account && window.rdt) window.rdt('track', 'SignUp');
    syncLanguageFromUser(data.user);
    return data;
  };

  const convertGuest = async (formData) => {
    const res = await api.post('/auth/convert-guest', formData);
    // Part 3: Guest stays guest — don't update user state
    return res.data;
  };

  const verifyGuestConversion = async (code) => {
    beginSessionTransition();
    // Token-issuing leg of the guest→registered conversion. The backend request
    // model carries the `client` field (auth.py:733) and delivers the rotated
    // session via _deliver_session(_is_native_client(...)) (auth.py:830): native
    // gets the bearer pair in the body (persisted by setTokens below), web gets
    // cookies — same contract as login/verify/guest. (The old F4.2b gap —
    // cookies-only, no `client` field — was closed backend-side; this comment
    // used to document it and went stale.)
    const res = await api.post('/auth/verify-guest-conversion', withNativeSignal({ code }));
    if (IS_APP_BUILD) await setTokens(res.data);
    // Frontera hermana de la bodega (F6.4a): la ÚNICA que CONSERVA cambiando
    // de id — misma persona, mismo dispositivo, continuidad explícita. Solo
    // se sobrescribe el dueño; no se borra nada.
    await syncWarehouseIdentity(WAREHOUSE_BOUNDARY.GUEST_CONVERTED, res.data.user.id);
    if (res.data.csrf_token) setCsrfToken(res.data.csrf_token);
    setUser(res.data.user);
    setLoading(false);
    identifyUser(res.data.user.id, { email: res.data.user.email, plan: res.data.user.plan, name: res.data.user.name });
    trackEvent('guest_converted');
    if (window.rdt) window.rdt('track', 'SignUp');
    syncLanguageFromUser(res.data.user);
    return res.data;
  };

  const resendGuestConversion = async () => {
    const res = await api.post('/auth/resend-guest-conversion');
    return res.data;
  };

  const cancelGuestConversion = async () => {
    try { await api.post('/auth/cancel-guest-conversion'); } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, loading, bootOffline, retryBootRefresh, login, signup, loginAsGuest, verifyEmail, resendVerification, logout, handleOAuthCallback, handleAppleCallback, convertGuest, handleNativeSocialLogin, verifyGuestConversion, resendGuestConversion, cancelGuestConversion, fetchUser, refreshUser: fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
